import type { Faq } from './industries';
import routes from './routes.json';

/*
 * Comparison and alternative pages: /compare/<a>-vs-<b> and /alternatives/<competitor>. One file per page in src/marketing/comparisons/<slug>.ts
 * (default export: a ComparisonPage); it is built but not published until its path is in routes.json and the sitemap. The rules the SEO plan sets are
 * checked by src/marketing/comparison-rules.ts and its tests: every fact about a competitor carries a source link and the date it was checked; no
 * superlatives, customer counts, ratings or savings claims; a fair "choose them if" section; a visible last-reviewed date; a trademark note.
 */
export interface Source {
  id: string;
  url: string;
  /** YYYY-MM-DD */
  checked: string;
  /** how the page was read ("fetched HTML", "rendered page") */
  how: string;
}

export interface Cell {
  text: string;
  /** Source ids; every fact about a competitor has at least one. */
  sources?: string[];
}

export interface ComparisonPage {
  slug: string;
  /** `/compare/<a>-vs-<b>` or `/alternatives/<x>` */
  path: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  /** <= 60 characters, ends with " | Scopie" */
  title: string;
  /** <= 155 characters */
  metaDescription: string;
  h1: string;
  /** Short link text for the footer ("Jobber alternative"); the h1 when absent. */
  navLabel?: string;
  /** 40-60 words, first text under the H1 */
  answerFirst: string;
  /** YYYY-MM-DD: shown on the page and sent as dateModified */
  lastReviewed: string;
  sections: { id: string; h2: string; body: string[] }[];
  /** `columns[0]` is the (empty) label column; each row has one cell per column. A column named Scopie needs no sources. */
  table: { caption: string; columns: string[]; rows: { label: string; cells: Cell[] }[] };
  faq: Faq[];
  sources: Source[];
  /** "X is a trademark of its owner; Scopie is not affiliated." Shown at the foot of the page. */
  disclaimer: string;
  internalLinks: { href: string; anchor: string; status: 'live' | 'planned' }[];
  cta: { heading: string; text: string; button: string; href: string; fine: string };
  /** Notes for the owner before publishing. Never rendered. */
  claimsToVerify: string[];
}

/** `alternative` for /alternatives/<x>, `compare` for /compare/<a>-vs-<b>. */
export const kindOf = (p: Pick<ComparisonPage, 'path'>): 'alternative' | 'compare' => (p.path.startsWith('/alternatives/') ? 'alternative' : 'compare');

export const comparisonPages: ComparisonPage[] = Object.values(import.meta.glob<{ default: ComparisonPage }>('./comparisons/*.ts', { eager: true })).map((m) => m.default);

/** The comparison pages that are published (listed in routes.json). */
/** What a footer link to the page says. */
export const navLabelOf = (p: ComparisonPage): string => p.navLabel ?? p.h1;

export const liveComparisons = (): ComparisonPage[] => comparisonPages.filter((p) => routes.routes.includes(p.path));
